'use client';

/**
 * MessengerTabsSection - Unified messaging compose with channel tabs
 *
 * Renders a segmented control for channel selection (WhatsApp, WeChat, Chatter)
 * with a collapsible drawer showing channel-specific compose UI.
 *
 * Schema-driven: channel labels, icons, colors come from CHANNEL_REGISTRY.
 *
 * @layer presentation
 * @arch_component_type section
 * @arch_domain copilot
 */

import { useState } from 'react';
import {
  Phone,
  MessageCircle,
  MessageSquarePlus,
  ChevronDown,
  ChevronUp,
  Send,
  Loader2,
  type LucideIcon,
} from 'lucide-react';
import { toast } from 'sonner';
import { cn } from '@/lib/utils/cn';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import { Button } from '@/components/ui/button';
import { Textarea } from '@/components/ui/textarea';
import type { ContactSummary, MessengerChannel, ChannelConfig, WhatsAppMediaAttachment } from '@/lib/api/types';
import { useChannelConfigs } from '@/lib/stores/total-matrix-store';
import { ContactSelector } from '../contact-selector';
import { WhatsAppAttachmentRow } from '../whatsapp-attachment-row';

// Icon map - resolves string icon names from schema to Lucide components
const ICON_MAP: Record<string, LucideIcon> = {
  phone: Phone,
  'message-circle': MessageCircle,
  'message-square-plus': MessageSquarePlus,
};

function getIcon(iconName: string): LucideIcon {
  return ICON_MAP[iconName] || MessageCircle;
}

interface MessengerTabsSectionProps {
  channels: MessengerChannel[];
  collapsedByDefault?: boolean;
  // Contact data for WhatsApp/WeChat
  contacts: ContactSummary[];
  contactsLoading?: boolean;
  // Callbacks
  onSendWhatsApp?: (phone: string, message: string, media?: WhatsAppMediaAttachment[]) => Promise<{ success: boolean; error?: string }>;
  onSendWeChat?: (wechatId: string, message: string) => Promise<{ success: boolean; error?: string }>;
  onPostToChatter?: (message: string) => Promise<{ success: boolean; message_id?: number; error?: string }>;
  // Loading states
  isSendingWhatsApp?: boolean;
  isSendingWeChat?: boolean;
  isPostingChatter?: boolean;
  // Optional context picker rendered above channel tabs when expanded
  contextPicker?: React.ReactNode;
}

// Fallback configs if schema not loaded (should rarely happen)
const FALLBACK_CONFIGS: Record<MessengerChannel, ChannelConfig> = {
  whatsapp: {
    key: 'whatsapp',
    label: 'WhatsApp',
    icon: 'phone',
    color: 'text-green-600',
    button_color: 'bg-green-600 hover:bg-green-700',
    placeholder: 'Type your message...',
    send_label: 'Send WhatsApp',
  },
  wechat: {
    key: 'wechat',
    label: 'WeChat',
    icon: 'message-circle',
    color: 'text-emerald-600',
    button_color: 'bg-emerald-600 hover:bg-emerald-700',
    placeholder: 'Type your message...',
    send_label: 'Send WeChat',
  },
  chatter: {
    key: 'chatter',
    label: 'Messaging',
    icon: 'message-square-plus',
    color: 'text-blue-600',
    button_color: '',
    placeholder: 'Add a note to the timeline...',
    send_label: 'Post Note',
  },
};

// Channel accent colors for active state indicators
const CHANNEL_ACCENTS: Record<string, string> = {
  whatsapp: 'border-green-500/50',
  wechat: 'border-emerald-500/50',
  chatter: 'border-blue-500/50',
};

export function MessengerTabsSection({
  channels,
  collapsedByDefault = true,
  contacts,
  contactsLoading = false,
  onSendWhatsApp,
  onSendWeChat,
  onPostToChatter,
  isSendingWhatsApp = false,
  isSendingWeChat = false,
  isPostingChatter = false,
  contextPicker,
}: MessengerTabsSectionProps) {
  // Get channel configs from schema store
  const schemaConfigs = useChannelConfigs();

  // Helper to get config (schema or fallback)
  const getConfig = (channel: MessengerChannel): ChannelConfig => {
    return schemaConfigs?.[channel] || FALLBACK_CONFIGS[channel];
  };

  const [isExpanded, setIsExpanded] = useState(!collapsedByDefault);
  const [activeTab, setActiveTab] = useState<MessengerChannel | undefined>(
    collapsedByDefault ? undefined : channels[0]
  );

  // Shared message state (persists across tabs)
  const [message, setMessage] = useState('');

  // WhatsApp specific state
  const [selectedContact, setSelectedContact] = useState<ContactSummary | null>(null);
  const [showManualPhone, setShowManualPhone] = useState(false);
  const [manualPhone, setManualPhone] = useState('');
  const [whatsappAttachments, setWhatsappAttachments] = useState<WhatsAppMediaAttachment[]>([]);

  // WeChat specific state (placeholder for future)
  const [wechatId, setWechatId] = useState('');

  const handleTabChange = (value: string) => {
    if (value === activeTab) {
      // Clicking active tab collapses
      setIsExpanded(false);
      setActiveTab(undefined);
    } else {
      setActiveTab(value as MessengerChannel);
      setIsExpanded(true);
    }
  };

  const handleToggleExpand = () => {
    if (isExpanded) {
      setIsExpanded(false);
      setActiveTab(undefined);
    } else {
      setIsExpanded(true);
      setActiveTab(channels[0]);
    }
  };

  const handleSendWhatsApp = async () => {
    const phone = showManualPhone ? manualPhone : selectedContact?.phone;
    if (!phone || !onSendWhatsApp) return;
    if (!message.trim() && whatsappAttachments.length === 0) return;

    const config = getConfig('whatsapp');
    const result = await onSendWhatsApp(
      phone,
      message.trim(),
      whatsappAttachments.length > 0 ? whatsappAttachments : undefined,
    );
    if (result.success) {
      toast.success(`${config.label} sent`, { description: `Sent to ${selectedContact?.name || phone}` });
      setMessage('');
      setWhatsappAttachments([]);
    } else {
      toast.error('Failed to send', { description: result.error || 'Unknown error' });
    }
  };

  const handleSendWeChat = async () => {
    if (!wechatId.trim() || !message.trim() || !onSendWeChat) return;

    const config = getConfig('wechat');
    const result = await onSendWeChat(wechatId.trim(), message.trim());
    if (result.success) {
      toast.success(`${config.label} sent`, { description: `Sent to ${wechatId}` });
      setMessage('');
    } else {
      toast.error('Failed to send', { description: result.error || 'Unknown error' });
    }
  };

  const handlePostChatter = async () => {
    if (!message.trim() || !onPostToChatter) return;

    const result = await onPostToChatter(message.trim());
    if (result.success) {
      const desc = result.message_id
        ? `Message #${result.message_id} posted to timeline`
        : 'Your note has been posted to the timeline';
      toast.success('Note added', { description: desc });
      setMessage('');
    } else {
      toast.error('Failed to post', { description: result.error || 'Unknown error' });
    }
  };

  const canSendWhatsApp =
    !!((showManualPhone && manualPhone.trim()) || (!showManualPhone && selectedContact?.phone)) &&
    (!!message.trim() || whatsappAttachments.length > 0) &&
    !isSendingWhatsApp &&
    !!onSendWhatsApp;

  const canSendWeChat = !!wechatId.trim() && !!message.trim() && !isSendingWeChat && !!onSendWeChat;

  const canPostChatter = !!message.trim() && !isPostingChatter && !!onPostToChatter;

  const accentBorder = activeTab ? CHANNEL_ACCENTS[activeTab] || 'border-border/50' : 'border-border/50';

  return (
    <div className={cn(
      'border-t-2 transition-colors duration-300',
      isExpanded ? accentBorder : 'border-border/50',
    )}>
      {/* Header — compose toggle */}
      <button
        onClick={handleToggleExpand}
        className={cn(
          'flex w-full items-center justify-between px-4 py-2.5 text-xs transition-colors',
          isExpanded
            ? 'bg-muted/40 text-foreground'
            : 'text-muted-foreground hover:bg-accent/30 hover:text-foreground',
        )}
      >
        <div className="flex items-center gap-2">
          <MessageSquarePlus className="h-3.5 w-3.5" />
          <span className="font-semibold tracking-wide uppercase text-[10px]">Compose Message</span>
        </div>
        {isExpanded ? (
          <ChevronDown className="h-3.5 w-3.5" />
        ) : (
          <ChevronUp className="h-3.5 w-3.5" />
        )}
      </button>

      {/* Expanded drawer */}
      {isExpanded && (
        <div className="px-4 pb-4 pt-2 bg-muted/20">
          {contextPicker}
          <Tabs value={activeTab} onValueChange={handleTabChange}>
            {/* Channel tabs */}
            <TabsList className="w-full h-9 bg-background/60 border border-border/50 rounded-lg p-0.5">
              {channels.map((channel) => {
                const config = getConfig(channel);
                const Icon = getIcon(config.icon);
                const isActive = activeTab === channel;
                return (
                  <TabsTrigger
                    key={channel}
                    value={channel}
                    className={cn(
                      'flex-1 gap-1.5 h-full rounded-md text-xs font-medium transition-all duration-200',
                      isActive && config.color,
                    )}
                  >
                    <Icon className="h-3.5 w-3.5" />
                    {config.label}
                  </TabsTrigger>
                );
              })}
            </TabsList>

            {/* WhatsApp Tab Content */}
            {channels.includes('whatsapp') && (
              <TabsContent value="whatsapp" className="mt-3 space-y-2.5">
                {(() => {
                  const config = getConfig('whatsapp');
                  return (
                    <>
                      {/* Contact selector */}
                      {!showManualPhone ? (
                        <div className="space-y-1.5">
                          <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                            Recipient
                          </label>
                          <ContactSelector
                            contacts={contacts}
                            selectedContact={selectedContact}
                            onSelect={setSelectedContact}
                            onManualEntry={() => setShowManualPhone(true)}
                            isLoading={contactsLoading}
                          />
                          {selectedContact && !selectedContact.phone && (
                            <p className="text-[11px] text-amber-600">This contact has no phone number</p>
                          )}
                        </div>
                      ) : (
                        <div className="space-y-1.5">
                          <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                            Phone Number
                          </label>
                          <div className="flex gap-2">
                            <input
                              type="tel"
                              placeholder="+1 234 567 8900"
                              value={manualPhone}
                              onChange={(e) => setManualPhone(e.target.value)}
                              className="flex-1 px-3 py-1.5 text-sm border border-border/50 rounded-lg bg-background focus:outline-none focus:ring-1 focus:ring-primary/30"
                            />
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-xs h-8"
                              onClick={() => {
                                setShowManualPhone(false);
                                setManualPhone('');
                              }}
                            >
                              Cancel
                            </Button>
                          </div>
                        </div>
                      )}

                      {/* Message + Send */}
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                          Message
                        </label>
                        <Textarea
                          placeholder={config.placeholder}
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          rows={2}
                          className="resize-none text-sm bg-background border-border/50 rounded-lg focus:ring-1 focus:ring-primary/30"
                        />
                      </div>
                      <WhatsAppAttachmentRow
                        attachments={whatsappAttachments}
                        onAttachmentsChange={setWhatsappAttachments}
                        disabled={isSendingWhatsApp}
                      />
                      <div className="flex justify-end pt-1">
                        <Button
                          size="sm"
                          onClick={handleSendWhatsApp}
                          disabled={!canSendWhatsApp}
                          className={cn(
                            'gap-1.5 rounded-lg text-xs h-8 px-4 text-white shadow-sm transition-all',
                            config.button_color,
                            !canSendWhatsApp && 'opacity-50',
                          )}
                        >
                          {isSendingWhatsApp ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Send className="h-3 w-3" />
                          )}
                          {config.send_label}
                        </Button>
                      </div>
                    </>
                  );
                })()}
              </TabsContent>
            )}

            {/* WeChat Tab Content */}
            {channels.includes('wechat') && (
              <TabsContent value="wechat" className="mt-3 space-y-2.5">
                {(() => {
                  const config = getConfig('wechat');
                  return (
                    <>
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                          WeChat ID
                        </label>
                        <input
                          type="text"
                          placeholder="WeChat ID"
                          value={wechatId}
                          onChange={(e) => setWechatId(e.target.value)}
                          className="w-full px-3 py-1.5 text-sm border border-border/50 rounded-lg bg-background focus:outline-none focus:ring-1 focus:ring-primary/30"
                        />
                      </div>
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                          Message
                        </label>
                        <Textarea
                          placeholder={config.placeholder}
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          rows={2}
                          className="resize-none text-sm bg-background border-border/50 rounded-lg focus:ring-1 focus:ring-primary/30"
                        />
                      </div>
                      <div className="flex justify-end pt-1">
                        <Button
                          size="sm"
                          onClick={handleSendWeChat}
                          disabled={!canSendWeChat}
                          className={cn(
                            'gap-1.5 rounded-lg text-xs h-8 px-4 text-white shadow-sm transition-all',
                            config.button_color,
                            !canSendWeChat && 'opacity-50',
                          )}
                        >
                          {isSendingWeChat ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Send className="h-3 w-3" />
                          )}
                          {config.send_label}
                        </Button>
                      </div>
                    </>
                  );
                })()}
              </TabsContent>
            )}

            {/* Chatter Tab Content */}
            {channels.includes('chatter') && (
              <TabsContent value="chatter" className="mt-3 space-y-2.5">
                {(() => {
                  const config = getConfig('chatter');
                  return (
                    <>
                      <div className="space-y-1.5">
                        <label className="text-[10px] font-medium text-muted-foreground uppercase tracking-wide">
                          Internal Note
                        </label>
                        <Textarea
                          placeholder={config.placeholder}
                          value={message}
                          onChange={(e) => setMessage(e.target.value)}
                          rows={2}
                          className="resize-none text-sm bg-background border-border/50 rounded-lg focus:ring-1 focus:ring-primary/30"
                        />
                      </div>
                      <div className="flex justify-end pt-1">
                        <Button
                          size="sm"
                          onClick={handlePostChatter}
                          disabled={!canPostChatter}
                          className={cn(
                            'gap-1.5 rounded-lg text-xs h-8 px-4 shadow-sm transition-all',
                            config.button_color || 'bg-primary hover:bg-primary/90 text-primary-foreground',
                            !canPostChatter && 'opacity-50',
                          )}
                        >
                          {isPostingChatter ? (
                            <Loader2 className="h-3.5 w-3.5 animate-spin" />
                          ) : (
                            <Send className="h-3 w-3" />
                          )}
                          {config.send_label}
                        </Button>
                      </div>
                    </>
                  );
                })()}
              </TabsContent>
            )}
          </Tabs>
        </div>
      )}
    </div>
  );
}
